import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface OpsUser {
  id: string;
  email: string;
  username: string;
  role: string;
  user_status: string;
  profile_image_url: string | null;
  is_ops_staff?: boolean;
}

// IR-1241: why the session ended, so the SessionController can redirect to
// /login with the right banner.
//   expired — an /auth/refresh/ genuinely failed (api.ts refresh-fail path)
//   idle    — the inactivity timer elapsed (use-idle-logout)
//   manual  — the operator clicked Sign out (use-logout)
export type LogoutReason = "expired" | "idle" | "manual";

interface AuthState {
  user: OpsUser | null;
  logoutReason: LogoutReason | null;
  setUser: (u: OpsUser | null) => void;
  logout: (reason?: LogoutReason) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      logoutReason: null,
      // Fresh sign-in wipes any stale logoutReason so a re-login never flashes
      // the "session expired" banner.
      setUser: (u) => set({ user: u, logoutReason: u ? null : get().logoutReason }),
      logout: (reason = "manual") => set({ user: null, logoutReason: reason }),
      // Back-compat: the api.ts refresh-fail path calls clear() — that is always
      // an expired session.
      clear: () => set({ user: null, logoutReason: "expired" }),
      isAuthenticated: () => !!get().user,
    }),
    {
      name: "iris-ops-auth",
      // Persist only the user; logoutReason is transient per-tab state.
      partialize: (s) => ({ user: s.user }),
    },
  ),
);
